/**
 * Cart Count Updater
 * Event-driven cart count sync with debounced API checks.
 */

(function() {
  'use strict';

  var HEADER_COUNT_SELECTOR = '.header__cart-count';
  var EMPTY_CLASS = 'header__cart-count--empty';
  var HAS_ITEMS_CLASS = 'header__cart-count--has-items';
  var MIN_SYNC_INTERVAL = 700;

  var syncTimer = null;
  var syncInFlight = false;
  var lastSyncAt = 0;
  var lastKnownCount = null;

  function parseCount(value) {
    var count = parseInt(value, 10);
    return Number.isNaN(count) ? 0 : count;
  }

  function getCountElements() {
    return Array.prototype.slice.call(document.querySelectorAll(HEADER_COUNT_SELECTOR));
  }

  function readCurrentCount() {
    var firstElement = getCountElements()[0];
    if (!firstElement) return 0;
    return parseCount(firstElement.textContent.trim());
  }

  function updateCountClasses(element, itemCount) {
    element.classList.remove(EMPTY_CLASS, HAS_ITEMS_CLASS);
    element.classList.add(itemCount === 0 ? EMPTY_CLASS : HAS_ITEMS_CLASS);
  }

  function dispatchCartUpdated(itemCount, source) {
    document.documentElement.dispatchEvent(new CustomEvent('ff:cart-updated', {
      bubbles: true,
      detail: {
        itemCount: itemCount,
        source: source || 'unknown'
      }
    }));
  }

  function renderCount(itemCount, source) {
    var countElements = getCountElements();
    if (!countElements.length) return;

    countElements.forEach(function(element) {
      if (element.textContent.trim() !== String(itemCount)) {
        element.textContent = itemCount;
      }
      updateCountClasses(element, itemCount);
    });

    lastKnownCount = itemCount;
    dispatchCartUpdated(itemCount, source);
  }

  function syncCountFromApi(source) {
    if (syncInFlight || typeof window.fetch !== 'function') return;
    if ((Date.now() - lastSyncAt) < MIN_SYNC_INTERVAL) return;

    lastSyncAt = Date.now();
    syncInFlight = true;

    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function(response) {
        if (!response.ok) throw new Error('Unable to fetch cart');
        return response.json();
      })
      .then(function(cart) {
        renderCount(parseCount(cart && cart.item_count), source || 'cart.js');
      })
      .catch(function() {
        renderCount(readCurrentCount(), 'dom-fallback');
      })
      .finally(function() {
        syncInFlight = false;
      });
  }

  function scheduleSync(source, delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function() {
      syncCountFromApi(source);
    }, typeof delay === 'number' ? delay : 150);
  }

  function handleProductAdded(event) {
    var quantity = parseCount(event && event.detail && event.detail.quantity);
    if (quantity < 1) quantity = 1;

    var currentCount = lastKnownCount === null ? readCurrentCount() : lastKnownCount;
    renderCount(currentCount + quantity, 'product:added');
    scheduleSync('product:added', 500);
  }

  function handleCartEvent(event) {
    var eventName = (event && event.type) || 'cart:event';
    if (eventName === 'cart:updated' && event && event.detail && typeof event.detail.itemCount !== 'undefined') {
      renderCount(parseCount(event.detail.itemCount), eventName);
      return;
    }

    scheduleSync(eventName, 140);
  }

  function installUiActionHints() {
    document.addEventListener('click', function(event) {
      var target = event.target;
      if (!target) return;

      if (
        target.closest('[data-action="remove-item"]') ||
        target.closest('.item-remove') ||
        target.closest('.cart__remove-button') ||
        target.closest('[data-cart-remove]') ||
        target.closest('a[href*="/cart/change"]') ||
        target.closest('[name="plus"]') ||
        target.closest('[name="minus"]')
      ) {
        scheduleSync('cart:ui-click', 420);
      }
    }, true);

    document.addEventListener('change', function(event) {
      var target = event.target;
      if (!target) return;

      if (
        target.matches('input[name="updates[]"]') ||
        target.matches('input[name="quantity"]') ||
        target.matches('input.quantity-selector__value') ||
        target.matches('[data-quantity-input]')
      ) {
        scheduleSync('cart:ui-change', 320);
      }
    }, true);
  }

  function init() {
    if (!getCountElements().length) return;

    renderCount(readCurrentCount(), 'init:dom');

    document.addEventListener('product:added', handleProductAdded);
    document.addEventListener('cart:refresh', handleCartEvent);
    document.addEventListener('cart:updated', handleCartEvent);

    window.addEventListener('pageshow', function() {
      scheduleSync('pageshow', 60);
    });

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible') {
        scheduleSync('visibilitychange', 80);
      }
    });

    installUiActionHints();
    scheduleSync('init:sync', 180);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
